import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthApiService } from '@api-new/authservice';
import { ComponentStore, OnStoreInit } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { ErrorModel } from '@shared-lib/models/error.model';
import { AuthService } from '@shared/services/auth.service';
import { brandingToSignInCompanyIdOrNetworkIdAdapter } from '@shared/store/auth/adapters/branding-to-sign-in-company-id-or-network-id.adapter';
import { sendVerificationCodeRequestAdapter } from '@shared/store/auth/adapters/send-verification-code-request.adapter';
import { loginFailure, newSignInEmailSent, pendingSignInSuccess, setSignInLinkCompanies } from '@shared/store/auth/auth.action';
import { selectBranding, selectIsLoadingBranding, selectPendingSignIn, selectRedirectUrl } from '@shared/store/auth/auth.selectors';
import { PendingSignInModel } from '@shared/store/auth/models/pending-sign-in.model';
import { isSignInCodeRejectedReason } from '@shared/store/auth/utils/is-sign-in-code-rejected-reason';
import { loadInitialPlatformData } from '@shared/store/common.actions';
import { Observable, combineLatest, switchMap, timer } from 'rxjs';
import { filter, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  LOGIN_STATUS_ANIMATION_LOOP_MS,
  LOGIN_STATUS_ESCAPE_LINK_DELAY_MS,
  LOGIN_STATUS_SUCCESS_HOLD_MS,
  LOGIN_STATUS_TIMEOUT_MS,
} from './constants/login-status-timing';
import { LoginStatusEnum } from './enums/login-status.enum';
import { SignInLinkModel } from './models/sign-in-link.model';
import { decodeSignInLink } from './utils/decode-sign-in-link';
import { getDelayUntilLoopBoundary } from './utils/get-delay-until-loop-boundary';

interface LoginStatusState {
  signInLink: SignInLinkModel | null;
  pendingSignIn: PendingSignInModel | null;
  status: LoginStatusEnum;
  nextStatus: LoginStatusEnum | null;
  loopStartedAt: number | null;
  hasEscapeLinkDelayPassed: boolean;
  hasCheckingTimedOut: boolean;
  isCancelled: boolean;
  hasNewSignInEmailFailed: boolean;
  loadingActions: {
    isSendingNewSignInEmail: boolean;
  };
}

const initialLoginStatusState: LoginStatusState = {
  signInLink: null,
  pendingSignIn: null,
  status: LoginStatusEnum.Checking,
  nextStatus: null,
  loopStartedAt: null,
  hasEscapeLinkDelayPassed: false,
  hasCheckingTimedOut: false,
  isCancelled: false,
  hasNewSignInEmailFailed: false,
  loadingActions: {
    isSendingNewSignInEmail: false,
  },
};

@Injectable()
export class LoginStatusStore extends ComponentStore<LoginStatusState> implements OnStoreInit {
  private readonly globalStore = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authApiService = inject(AuthApiService);
  private readonly authService = inject(AuthService);

  private readonly prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor() {
    super(initialLoginStatusState);
  }

  // SELECTORS ---------------------------------------------------------------------------------------------------------
  readonly signInLink$: Observable<SignInLinkModel | null> = this.select((state) => state.signInLink);

  readonly pendingSignIn$: Observable<PendingSignInModel | null> = this.select((state) => state.pendingSignIn);

  readonly redirectUrl$: Observable<string> = this.select(
    this.signInLink$,
    this.globalStore.select(selectRedirectUrl),
    (signInLink, savedRedirectUrl) => signInLink?.redirectUrl || savedRedirectUrl || `/${paths.PLATFORM}`,
  );

  readonly status$: Observable<LoginStatusEnum> = this.select((state) => state.status);

  readonly nextStatus$: Observable<LoginStatusEnum | null> = this.select((state) => state.nextStatus);

  readonly isCheckingOutcomeKnown$: Observable<boolean> = this.select(
    (state) => state.nextStatus !== null || state.status !== LoginStatusEnum.Checking,
  );

  readonly loopStartedAt$: Observable<number | null> = this.select((state) => state.loopStartedAt);

  readonly hasEscapeLinkDelayPassed$: Observable<boolean> = this.select((state) => state.hasEscapeLinkDelayPassed);

  readonly isEscapeLinkVisible$: Observable<boolean> = this.select(
    this.status$,
    this.hasEscapeLinkDelayPassed$,
    (status, hasEscapeLinkDelayPassed) =>
      hasEscapeLinkDelayPassed || status === LoginStatusEnum.Error || status === LoginStatusEnum.Expired,
  );

  readonly checkingTimedOut$: Observable<boolean> = this.select((state) => state.hasCheckingTimedOut).pipe(filter(Boolean));

  readonly cancelled$: Observable<boolean> = this.select((state) => state.isCancelled).pipe(filter(Boolean));

  readonly canSendNewSignInEmail$: Observable<boolean> = this.select(this.signInLink$, (signInLink) => signInLink !== null);

  readonly hasNewSignInEmailFailed$: Observable<boolean> = this.select((state) => state.hasNewSignInEmailFailed);

  readonly isSendingNewSignInEmail$: Observable<boolean> = this.select((state) => state.loadingActions.isSendingNewSignInEmail);

  // EFFECTS -----------------------------------------------------------------------------------------------------------
  readonly initialise = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      map(() => this.route.snapshot.queryParamMap.get('data')),
      withLatestFrom(this.globalStore.select(selectPendingSignIn)),
      tap(([signInLinkData, pendingSignIn]) => {
        if (signInLinkData !== null) {
          this.startLinkSignIn(decodeSignInLink(signInLinkData));
          return;
        }
        if (!pendingSignIn) {
          void this.router.navigate(['/', paths.AUTH, paths.auth.LOGIN]);
          return;
        }
        this.setPendingSignIn(pendingSignIn);
        this.startChecking();
        this.signIn();
      }),
    ),
  );

  readonly startLinkSignIn = this.effect((signInLink$: Observable<SignInLinkModel | null>) =>
    signInLink$.pipe(
      tap((signInLink) => this.setSignInLink(signInLink)),
      withLatestFrom(this.redirectUrl$),
      tap(([signInLink, redirectUrl]) => {
        const signedInEmail = this.authService.tokenValid() ? this.authService.emailFromToken() : undefined;
        if (signInLink && signedInEmail?.toLowerCase() === signInLink.email.toLowerCase()) {
          void this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
          return;
        }

        this.startChecking();
        if (signInLink) {
          this.listSignInCompanies();
        } else {
          this.setNextStatus(LoginStatusEnum.Expired);
        }
      }),
    ),
  );

  readonly startChecking = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => {
        this.resetChecking();
        this.switchStatusOnLoopBoundary();
        this.showEscapeLinkAfterDelay();
        this.timeOutChecking();
      }),
    ),
  );

  readonly listSignInCompanies = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() =>
        this.globalStore.select(selectIsLoadingBranding).pipe(
          filter((isLoadingBranding) => !isLoadingBranding),
          take(1),
          withLatestFrom(this.signInLink$, this.globalStore.select(selectBranding)),
          switchMap(([, { email, code, signInCompanyId, redirectUrl }, branding]) =>
            this.authApiService
              .HTTP_CP_ListSignInCompanies({ email, code, ...brandingToSignInCompanyIdOrNetworkIdAdapter(branding, signInCompanyId) })
              .pipe(
                tapResponse({
                  next: ({ companies }) => {
                    if (!companies?.length) {
                      this.setNextStatus(LoginStatusEnum.Expired);
                      return;
                    }
                    if (companies.length > 1) {
                      this.globalStore.dispatch(setSignInLinkCompanies({ email, code, signInCompanyId, redirectUrl, companies }));
                      void this.router.navigate(['/', paths.AUTH, paths.auth.ACCOUNT_SELECT]);
                      return;
                    }
                    this.setPendingSignIn({ email, code, profileCompanyId: companies[0].id, signInCompanyId });
                    this.signIn();
                  },
                  error: (error: ErrorModel) =>
                    this.setNextStatus(isSignInCodeRejectedReason(error.error?.reason) ? LoginStatusEnum.Expired : LoginStatusEnum.Error),
                }),
              ),
          ),
          takeUntil(this.checkingTimedOut$),
        ),
      ),
      takeUntil(this.cancelled$),
    ),
  );

  readonly signIn = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      withLatestFrom(this.pendingSignIn$, this.signInLink$, this.globalStore.select(selectBranding)),
      switchMap(([, { email, code, profileCompanyId, signInCompanyId }, signInLink, branding]) =>
        this.authApiService
          .HTTP_CP_SignIn_WithCode({
            email,
            code,
            ...brandingToSignInCompanyIdOrNetworkIdAdapter(branding, signInCompanyId),
            profileCompanyId,
          })
          .pipe(
            tapResponse({
              next: (signInResponse) => {
                this.globalStore.dispatch(pendingSignInSuccess({ signInResponse }));
                this.globalStore.dispatch(loadInitialPlatformData());
                this.setNextStatus(LoginStatusEnum.Success);
              },
              error: (error: ErrorModel) => {
                if (!isSignInCodeRejectedReason(error.error?.reason)) {
                  this.setNextStatus(LoginStatusEnum.Error);
                  return;
                }
                if (signInLink) {
                  this.setNextStatus(LoginStatusEnum.Expired);
                  return;
                }
                this.globalStore.dispatch(loginFailure({ error }));
                void this.router.navigate(['/', paths.AUTH, paths.auth.LOGIN_VERIFICATION]);
              },
            }),
            takeUntil(this.checkingTimedOut$),
          ),
      ),
      takeUntil(this.cancelled$),
    ),
  );

  readonly retrySignIn = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      withLatestFrom(this.signInLink$, this.pendingSignIn$),
      tap(([, signInLink, pendingSignIn]) => {
        this.startChecking();
        if (signInLink && !pendingSignIn) {
          this.listSignInCompanies();
        } else {
          this.signIn();
        }
      }),
    ),
  );

  readonly timeOutChecking = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() => timer(LOGIN_STATUS_TIMEOUT_MS).pipe(takeUntil(this.isCheckingOutcomeKnown$.pipe(filter(Boolean))))),
      tap(() => {
        this.setHasCheckingTimedOut(true);
        this.setNextStatus(LoginStatusEnum.Error);
      }),
      takeUntil(this.cancelled$),
    ),
  );

  readonly switchStatusOnLoopBoundary = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() => combineLatest([this.loopStartedAt$, this.nextStatus$])),
      filter(([loopStartedAt, nextStatus]) => nextStatus !== null && (this.prefersReducedMotion || loopStartedAt !== null)),
      switchMap(([loopStartedAt, nextStatus]) =>
        timer(
          this.prefersReducedMotion ? 0 : getDelayUntilLoopBoundary(loopStartedAt, performance.now(), LOGIN_STATUS_ANIMATION_LOOP_MS),
        ).pipe(map(() => nextStatus)),
      ),
      tap((nextStatus) => {
        this.applyNextStatus();
        if (nextStatus === LoginStatusEnum.Success) {
          this.redirectAfterSuccess();
        }
      }),
      takeUntil(this.cancelled$),
    ),
  );

  readonly redirectAfterSuccess = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() => timer(LOGIN_STATUS_SUCCESS_HOLD_MS)),
      withLatestFrom(this.redirectUrl$),
      tap(([, redirectUrl]) => void this.router.navigateByUrl(redirectUrl)),
      takeUntil(this.cancelled$),
    ),
  );

  readonly showEscapeLinkAfterDelay = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() => timer(LOGIN_STATUS_ESCAPE_LINK_DELAY_MS)),
      tap(() => this.setHasEscapeLinkDelayPassed(true)),
      takeUntil(this.cancelled$),
    ),
  );

  readonly sendNewSignInEmail = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => {
        this.setHasNewSignInEmailFailed(false);
        this.setIsSendingNewSignInEmail(true);
      }),
      withLatestFrom(this.signInLink$, this.globalStore.select(selectBranding)),
      switchMap(([, { email, signInCompanyId }, branding]) =>
        this.authApiService.HTTP_CP_ProvideClientUserSignInCode(sendVerificationCodeRequestAdapter(email, branding, signInCompanyId)).pipe(
          tapResponse({
            next: () => {
              this.setIsSendingNewSignInEmail(false);
              this.globalStore.dispatch(newSignInEmailSent({ email, signInCompanyId }));
              void this.router.navigate(['/', paths.AUTH, paths.auth.LOGIN_VERIFICATION]);
            },
            error: () => {
              this.setIsSendingNewSignInEmail(false);
              this.setHasNewSignInEmailFailed(true);
            },
          }),
        ),
      ),
      takeUntil(this.cancelled$),
    ),
  );

  readonly goToLoginPage = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => {
        this.setIsCancelled(true);
        this.authService.logout();
        void this.router.navigate(['/', paths.AUTH, paths.auth.LOGIN]);
      }),
    ),
  );

  // UPDATERS ----------------------------------------------------------------------------------------------------------
  readonly setSignInLink = this.updater<SignInLinkModel | null>((state, signInLink): LoginStatusState => ({ ...state, signInLink }));

  readonly setPendingSignIn = this.updater<PendingSignInModel>((state, pendingSignIn): LoginStatusState => ({ ...state, pendingSignIn }));

  readonly setNextStatus = this.updater<LoginStatusEnum>((state, nextStatus): LoginStatusState => ({ ...state, nextStatus }));

  readonly applyNextStatus = this.updater(
    (state): LoginStatusState => ({ ...state, status: state.nextStatus ?? state.status, nextStatus: null }),
  );

  readonly setFirstLoopStartedAt = this.updater<number>(
    (state, loopStartedAt): LoginStatusState => ({ ...state, loopStartedAt: state.loopStartedAt ?? loopStartedAt }),
  );

  readonly resetChecking = this.updater(
    (state): LoginStatusState => ({
      ...state,
      status: LoginStatusEnum.Checking,
      nextStatus: null,
      loopStartedAt: null,
      hasEscapeLinkDelayPassed: false,
      hasCheckingTimedOut: false,
    }),
  );

  readonly setHasEscapeLinkDelayPassed = this.updater<boolean>(
    (state, hasEscapeLinkDelayPassed): LoginStatusState => ({ ...state, hasEscapeLinkDelayPassed }),
  );

  readonly setHasCheckingTimedOut = this.updater<boolean>(
    (state, hasCheckingTimedOut): LoginStatusState => ({ ...state, hasCheckingTimedOut }),
  );

  readonly setIsCancelled = this.updater<boolean>((state, isCancelled): LoginStatusState => ({ ...state, isCancelled }));

  readonly setHasNewSignInEmailFailed = this.updater<boolean>(
    (state, hasNewSignInEmailFailed): LoginStatusState => ({ ...state, hasNewSignInEmailFailed }),
  );

  readonly setIsSendingNewSignInEmail = this.updater<boolean>(
    (state, isSendingNewSignInEmail): LoginStatusState => ({
      ...state,
      loadingActions: { ...state.loadingActions, isSendingNewSignInEmail },
    }),
  );

  ngrxOnStoreInit(): void {
    this.initialise();
  }
}
